import { useMutation, useQueryClient } from '@tanstack/react-query';
import BottomModal from '../../../components/BottomModal';
import { updateOrder } from '../../../services/api';
import { toast } from '../../../components/UpToast';
import type { Customer, OrderOrder } from '../../../types/codexOrder';
import { ApiConstants } from '../../../constants/ApiConstants';
import { LoadingIcon, PayTimeIcon, PhoneIcon } from '../../../assets/Icons';

function PaymentTypeModal({
  order,
  isOpen,
  onClose,
  orderId,
  customer,
}: {
  orderId: string;
  isOpen: boolean;
  order: OrderOrder;
  onClose: () => void;
  customer: Customer | undefined;
}) {
  const queryClient = useQueryClient();
  const { mutate: callUpdateOrder, isPending } = useMutation({
    mutationFn: (params: unknown) => updateOrder(params),
    onSuccess: resp => {
      queryClient.setQueryData(['getOrderDetails', orderId], resp);
      onClose();
    },
    onError: err => {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.info(
        error?.response?.data?.detail ??
          'Something went wrong. Order update action failed.',
      );
    },
  });

  function onPaymentUpdate(mode: string) {
    if (isPending) {
      return;
    }

    if (order?.details?.pay_by === mode) {
      onClose();
      return;
    }

    const params = {
      payment_option: mode,
      reference_id: orderId,
      order_type: order?.details?.order_type,
    } as {
      order_type: string;
      reference_id: string;
      payment_option: string;
      address_id?: string;
    };

    if (
      customer?.address?.id &&
      order?.details?.order_type === ApiConstants.TYPE_DELIVERY
    ) {
      params.address_id = String(customer?.address?.id);
    }

    callUpdateOrder(params);
  }

  return (
    <BottomModal isOpen={isOpen} onClose={onClose} height="36%">
      <div className="pb-2 px-2">
        <p className="text-lg font-bold mb-6">Pay Now or Later</p>
        {isPending ? (
          <div className="flex items-center justify-center mt-20">
            <LoadingIcon className="w-8 h-8 text-brand-icon" />
          </div>
        ) : (
          <>
            <div
              className="mb-3 p-4 rounded-lg bg-white cursor-pointer flex items-center gap-x-3 text-sm"
              onClick={() => {
                onPaymentUpdate(ApiConstants.PAY_BY_LINK);
              }}>
              <div className="px-3 py-1.5 bg-up-black-5 border border-up-black-20 rounded-lg shrink-0">
                <PhoneIcon className="w-6 h-6" />
              </div>
              <p>Pay right now</p>
            </div>
            <div
              className="p-4 rounded-lg bg-white cursor-pointer flex items-center gap-x-3 text-sm"
              onClick={() => {
                onPaymentUpdate(ApiConstants.PAY_BY_CASH);
              }}>
              <div className="px-3 py-1.5 bg-up-black-5 border border-up-black-20 rounded-lg shrink-0">
                <PayTimeIcon className="w-6 h-6" />
              </div>
              <p>Pay on {order?.details?.order_type || ''}</p>
            </div>
          </>
        )}
      </div>
    </BottomModal>
  );
}

export default PaymentTypeModal;
